/**
 * Chart colors utility - provides centralized access to chart colors from CSS variables.
 * These colors are defined in index.css and support both light and dark modes.
 */

// CSS variable references for use in inline styles
// Use these when you need to reference colors in JSX style attributes
export const CHART_COLOR_VARS = {
  chart1: "var(--chart-1)",
  chart2: "var(--chart-2)",
  chart3: "var(--chart-3)",
  chart4: "var(--chart-4)",
  chart5: "var(--chart-5)",
  chart6: "var(--chart-6)",
  chart7: "var(--chart-7)",
  chart8: "var(--chart-8)",
  chart9: "var(--chart-9)",
  chart10: "var(--chart-10)",
  positive: "var(--chart-positive)",
  neutral: "var(--chart-neutral)",
  negative: "var(--chart-negative)",
  mainBrand: "var(--chart-main-brand)",
  usage: "var(--chart-usage)",
  // Radial/Metric chart colors
  visibility: "var(--chart-visibility)",
  sentiment: "var(--chart-sentiment)",
  mentions: "var(--chart-mentions)",
  ranking: "var(--chart-ranking)",
} as const;

// Array of chart color CSS variables for multi-series charts
// Main brand color is first, followed by other series colors
export const CHART_COLORS = [
  CHART_COLOR_VARS.chart1, // navy
  CHART_COLOR_VARS.chart2, // teal
  CHART_COLOR_VARS.chart3, // mint
  CHART_COLOR_VARS.chart4, // chartreuse
  CHART_COLOR_VARS.chart5, // coral
  CHART_COLOR_VARS.chart6, // amber
  CHART_COLOR_VARS.chart7, // steel blue
  CHART_COLOR_VARS.chart8, // violet
  CHART_COLOR_VARS.chart9, // emerald
  CHART_COLOR_VARS.chart10, // cyan
] as const;

// Semantic colors for sentiment charts
export const SENTIMENT_COLORS = {
  positive: CHART_COLOR_VARS.positive,
  neutral: CHART_COLOR_VARS.neutral,
  negative: CHART_COLOR_VARS.negative,
} as const;

// Main brand color for charts
export const MAIN_BRAND_COLOR = CHART_COLOR_VARS.mainBrand;

// Usage chart color
export const USAGE_CHART_COLOR = CHART_COLOR_VARS.usage;

// Radial/Metric chart colors
export const RADIAL_CHART_COLORS = {
  visibility: CHART_COLOR_VARS.visibility,
  sentiment: CHART_COLOR_VARS.sentiment,
  mentions: CHART_COLOR_VARS.mentions,
  ranking: CHART_COLOR_VARS.ranking,
} as const;

let colorProbeElement: HTMLSpanElement | null = null;

function getColorProbeElement(): HTMLSpanElement | null {
  if (typeof document === "undefined") {
    return null;
  }

  if (!document.body) {
    return null;
  }

  if (!colorProbeElement || !document.body.contains(colorProbeElement)) {
    colorProbeElement = document.createElement("span");
    colorProbeElement.setAttribute("aria-hidden", "true");
    colorProbeElement.style.position = "absolute";
    colorProbeElement.style.width = "0";
    colorProbeElement.style.height = "0";
    colorProbeElement.style.overflow = "hidden";
    colorProbeElement.style.opacity = "0";
    colorProbeElement.style.pointerEvents = "none";
    document.body.appendChild(colorProbeElement);
  }

  return colorProbeElement;
}

/**
 * Resolve CSS custom-property colors into browser-computed values that are safe
 * to hand to SVG attributes such as Recharts `stroke` / `fill`.
 */
export function resolveCssColor(color: string): string {
  if (typeof document !== "undefined" && color.startsWith("var(") && color.endsWith(")")) {
    const variableName = color.slice(4, -1).trim();
    const variableValue = getComputedStyle(document.documentElement)
      .getPropertyValue(variableName)
      .trim();

    if (variableValue) {
      return resolveCssColor(variableValue);
    }
  }

  const probe = getColorProbeElement();

  if (!probe) {
    return color;
  }

  probe.style.color = "";
  probe.style.color = color;

  const resolvedColor = getComputedStyle(probe).color;
  return resolvedColor || color;
}

/**
 * Get a chart color by index (wraps around if index exceeds available colors)
 */
export function getChartColor(index: number): string {
  return resolveCssColor(CHART_COLORS[index % CHART_COLORS.length]);
}

/**
 * Get a color for a data series, with main brand getting the first color
 * and competitors getting subsequent colors
 */
export function getBrandChartColor(isMainBrand: boolean, competitorIndex: number): string {
  if (isMainBrand) {
    return resolveCssColor(MAIN_BRAND_COLOR);
  }
  // Skip the first color (main brand) for competitors
  const competitorColors = CHART_COLORS.slice(1);
  return resolveCssColor(competitorColors[competitorIndex % competitorColors.length]);
}
